import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ParentAuthProvider } from "@/context/ParentAuthContext";
import { CategoriesProvider } from "@/context/CategoriesContext";
import { Toaster } from "@/components/ui/sonner";

import PublicLayout from "@/components/PublicLayout";
import AdminLayout from "@/components/AdminLayout";
import Home from "@/pages/Home";
import Competitions from "@/pages/Competitions";
import CompetitionDetail from "@/pages/CompetitionDetail";
import SummerCamp from "@/pages/SummerCamp";
import Sponsors from "@/pages/Sponsors";
import Contact from "@/pages/Contact";
import AuthCallback from "@/pages/AuthCallback";
import Register from "@/pages/Register";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ParentSignup from "@/pages/ParentSignup";
import ParentLogin from "@/pages/ParentLogin";
import AdminAccess from "@/pages/admin/AdminAccess";
import Accounts from "@/pages/admin/Accounts";
import ParentLayout from "@/components/ParentLayout";
import ParentOverview from "@/pages/parent/Overview";
import ParentCubers from "@/pages/parent/Cubers";
import ParentChildProfile from "@/pages/parent/ChildProfile";
import ParentRegisterChild from "@/pages/parent/RegisterChild";
import ParentSettings from "@/pages/parent/Settings";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SignIn from "@/pages/SignIn";
import CheckIn from "@/pages/CheckIn";
import EventInvite from "@/pages/EventInvite";
import Dashboard from "@/pages/admin/Dashboard";
import Events from "@/pages/admin/Events";
import EventDetail from "@/pages/admin/EventDetail";
import Competitors from "@/pages/admin/Competitors";
import Preliminary from "@/pages/admin/Preliminary";
import Official from "@/pages/admin/Official";
import Submissions from "@/pages/admin/Submissions";
import SponsorsAdmin from "@/pages/admin/SponsorsAdmin";
import PastEvents from "@/pages/PastEvents";
import DonationSuccess from "@/pages/DonationSuccess";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen grid place-items-center text-[#7A7D8C]">
      <div data-testid="auth-loading">Checking session…</div>
    </div>
  );
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/competitions" element={<Competitions />} />
        <Route path="/competitions/:id" element={<CompetitionDetail />} />
        <Route path="/summer-camp" element={<SummerCamp />} />
        <Route path="/sponsors" element={<Sponsors />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/checkin/:eventId" element={<CheckIn />} />
        <Route path="/events/invite/:token" element={<EventInvite />} />
        <Route path="/register" element={<Register />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/donate/thanks" element={<DonationSuccess />} />
        <Route path="/parent/signup" element={<ParentSignup />} />
        <Route path="/parent/login" element={<ParentLogin />} />
        <Route path="/parent/my-cubers" element={<Navigate to="/parent" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/parent" element={<ParentLayout />}>
          <Route index element={<ParentOverview />} />
          <Route path="cubers" element={<ParentCubers />} />
          <Route path="cubers/:id" element={<ParentChildProfile />} />
          <Route path="register" element={<ParentRegisterChild />} />
          <Route path="settings" element={<ParentSettings />} />
        </Route>
      </Route>
      <Route path="/admin/login" element={<Navigate to="/signin?tab=organizer" replace />} />
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="access" element={<AdminAccess />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="events" element={<Events />} />
        <Route path="events/:id" element={<EventDetail />} />
        <Route path="competitors" element={<Competitors />} />
        <Route path="archive" element={<PastEvents />} />
        <Route path="preliminary" element={<Preliminary />} />
        <Route path="official" element={<Official />} />
        <Route path="submissions" element={<Submissions />} />
        <Route path="sponsors" element={<SponsorsAdmin />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ParentAuthProvider>
            <CategoriesProvider>
              <AppRouter />
              <Toaster position="top-right" richColors />
            </CategoriesProvider>
          </ParentAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
